/** @file Builds manifest.json: identity, profile, clip and sheet maps, size and hash of every other file. */
import type { EffectiveProfile } from '@zilo/pose3d-format/profile/effectiveProfile';
import { hashBytes } from '@zilo/pose3d-export/hash/hashBytes';
import { utf8 } from '@zilo/pose3d-export/text/utf8';
import type { BundleManifest } from '@zilo/pose3d-export/types/bundle';
import type { ExportDeps, ExportInput } from '@zilo/pose3d-export/types/exportDeps';

/** The bundle path of the manifest; never listed in its own `files`. */
const MANIFEST_PATH = 'manifest.json';

/**
 * The manifest of `input` whose bundle holds `files` (strings are hashed as UTF-8); `files` is sorted by path,
 * `clips` maps each clip name to `clips/<name>.json`, `createdAt` is `deps.now`.
 */
export function buildManifest(
  input: ExportInput, profile: EffectiveProfile, sheets: BundleManifest['sheets'], files: Record<string, Uint8Array | string>,
  deps: Pick<ExportDeps, 'hash' | 'hashAlgo' | 'now'>,
): BundleManifest {
  let algo: BundleManifest['hashAlgo'] = deps.hashAlgo ?? 'fnv1a64';
  const listed: BundleManifest['files'] = {};
  for (const path of Object.keys(files).sort()) {
    if (path === MANIFEST_PATH) continue;
    const content = files[path] ?? '';
    const bytes = typeof content === 'string' ? utf8(content) : content;
    const hashed = hashBytes(bytes, deps);
    algo = hashed.algo;
    listed[path] = { bytes: bytes.length, hash: hashed.hash };
  }
  const clips = Object.fromEntries(Object.keys(input.clips).sort().map((name) => [name, `clips/${name}.json`]));
  return {
    format: 'pose3d/bundle@1', id: input.id, name: input.name, revision: input.revision, createdAt: deps.now,
    puppet: 'puppet.pose3d.json', profile, clips, sheets, hashAlgo: algo, files: listed,
  };
}
